import React from 'react';
import { Card } from 'react-bootstrap';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';

// Blood Clotting & Flow → Insights tab. Shows how the estimated whole-blood
// viscosity (high shear, 208 s⁻¹) was calculated for THIS test: the de Simone
// formula with the patient's own hematocrit and total protein (g/dL) substituted
// in. Client-facing and non-diagnostic — it explains the number, it doesn't
// interpret it. Total protein is g/dL here (NOT g/L — see BloodworkAnalysis
// populateViscosityPanelMarker for the unit-error history).

// Round to 2 decimals, then drop trailing zeros so entered values read cleanly
// (45 not 45.00, 7.2 not 7.20) while computed terms keep 2-decimal precision.
const trim = (n: number): string => String(Math.round(n * 100) / 100);
const fixed2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);

const formulaStyle: React.CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '14px',
  lineHeight: 1.9,
  color: '#20527f',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  display: 'block',
};

const noteHeadingStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '13px',
  color: '#20527f',
  margin: '0 0 6px',
};

const noteStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#5b6675',
  lineHeight: 1.6,
  margin: '0 0 10px',
};

const sensitivityTableStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#5b6675',
  borderCollapse: 'collapse',
  margin: '0 0 10px',
};

const sensitivityCellStyle: React.CSSProperties = {
  padding: '3px 14px 3px 0',
  borderBottom: '1px solid #eef1f4',
  whiteSpace: 'nowrap',
};

// How far each input moves the estimate across a typical clinical span —
// 0.12 × (50 − 36) ≈ 1.7 cP for HCT vs 0.17 × (8.3 − 6.0) ≈ 0.4 cP for total
// protein. Kept as data so the card and the Notes digest can't drift apart.
const SENSITIVITY_ROWS = [
  { input: 'Hematocrit', span: '36 → 50 %', effect: 'moves the estimate ≈ 1.7 cP' },
  {
    input: 'Total protein',
    span: '6.0 → 8.3 g/dL',
    effect: 'moves the estimate ≈ 0.4 cP',
  },
];

// Client-facing framing for why this marker sits beside platelets, fibrinogen
// and D-Dimer even when it disagrees with them. Viscosity describes flow
// resistance (the "stasis" arm); the clotting markers describe clot formation
// and breakdown. Observational and non-diagnostic per LabSmarts copy rules.
const COMPANION_INTRO =
  'This estimate describes how easily blood flows. Platelets, fibrinogen, and ' +
  'D-Dimer describe how readily blood forms a clot and breaks it down again. ' +
  'Both belong in this section, but they describe different properties and can ' +
  'move independently of one another.';

const COMPANION_OUTRO =
  'Red-cell concentration is the dominant input — across a typical range it ' +
  'moves this estimate about four times more than total protein does. ' +
  'Fibrinogen makes up only a small fraction of total protein, so even a large ' +
  'change in fibrinogen shifts this number very little. A viscosity result ' +
  'above or below optimal alongside optimal platelets, fibrinogen, and D-Dimer ' +
  'is an expected combination, and points toward red-cell concentration and ' +
  'plasma protein rather than toward clotting activity.';

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export class ViscosityInsights extends React.Component<Props, {}> {
  static shouldRender(bloodworkAnalysis: BloodworkAnalysis): boolean {
    const v = bloodworkAnalysis && bloodworkAnalysis.viscosityCapiliaries;
    return !!(v && v.hasValue());
  }

  // Shared formula computation for both the rendered card and the Notes digest.
  static compute(bloodworkAnalysis: BloodworkAnalysis): {
    hct: number;
    protein: number;
    term1: number;
    term2: number;
    result: number;
  } | null {
    if (!ViscosityInsights.shouldRender(bloodworkAnalysis)) {
      return null;
    }
    const hct = bloodworkAnalysis.hct.value;
    const protein = bloodworkAnalysis.protein.value;
    return {
      hct,
      protein,
      term1: 0.12 * hct,
      term2: 0.17 * protein,
      result: bloodworkAnalysis.viscosityCapiliaries.value,
    };
  }

  // Plain-text version of the insight for the Notes "gather from tabs" digest,
  // so Blood Clotting & Flow's Insights reads into Notes like other sections'.
  static digestGroup(
    bloodworkAnalysis: BloodworkAnalysis,
  ): { title: string; items: string[] } | null {
    const c = ViscosityInsights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    // Mirror the rendered card's content (render, below) as plain-text lines so
    // the digest shows the SAME information the Insights tab shows.
    return {
      title: 'Viscosity - Estimated',
      items: [
        "Viscosity is not measured directly — it is estimated from this test's " +
          'hematocrit and total protein using the de Simone whole-blood viscosity ' +
          'formula (high shear, 208 s⁻¹): 0.12 × HCT + 0.17 × Total Protein − 2.07',
        `Using this test's values (HCT = ${trim(c.hct)}%, Total Protein = ${trim(
          c.protein,
        )} g/dL):`,
        `= 0.12 × ${trim(c.hct)} + 0.17 × ${trim(c.protein)} − 2.07`,
        `= ${fixed2(c.term1)} + ${fixed2(c.term2)} − 2.07`,
        `= ${fixed2(c.result)} cP`,
        'cP stands for centipoise, the standard unit of viscosity — a measure of ' +
          'how thick a fluid is and how strongly it resists flowing. A higher cP ' +
          'means thicker, more slowly-flowing blood; the value is best read ' +
          'against the reference range rather than on its own.',
        'Reading this alongside the clotting markers: ' + COMPANION_INTRO,
        ...SENSITIVITY_ROWS.map(
          r => `${r.input} (${r.span}) ${r.effect}`,
        ),
        COMPANION_OUTRO,
      ],
    };
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    const c = ViscosityInsights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    const { hct, protein, term1, term2, result } = c;

    return (
      <Card style={{ marginBottom: 12 }}>
        <Card.Header style={{ fontWeight: 'bold' }}>
          Viscosity - Estimated
        </Card.Header>
        <Card.Body>
          <p style={{ marginBottom: 12 }}>
            Viscosity is not measured directly.
            <br />
            It is estimated from this test's <strong>hematocrit</strong> and{' '}
            <strong>total protein</strong> using the de Simone whole-blood
            viscosity formula (high shear, 208 s⁻¹):
          </p>
          <code style={formulaStyle}>
            0.12 × HCT + 0.17 × Total Protein − 2.07
          </code>
          <p style={{ margin: '10px 0 6px', color: '#5b6675' }}>
            Using this test's values (HCT = {trim(hct)}%, Total Protein ={' '}
            {trim(protein)} g/dL):
          </p>
          <code style={formulaStyle}>
            = 0.12 × {trim(hct)} + 0.17 × {trim(protein)} − 2.07
            <br />
            = {fixed2(term1)} + {fixed2(term2)} − 2.07
            <br />
            <strong>= {fixed2(result)} cP</strong>
          </code>
          <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#5b6675' }}>
            <strong>cP</strong> stands for <strong>centipoise</strong>, the
            standard unit of viscosity — a measure of how thick a fluid is and
            how strongly it resists flowing. A higher cP means thicker,
            more slowly-flowing blood; the value is best read against the
            reference range rather than on its own.
          </p>

          <hr style={{ margin: '16px 0 12px', borderColor: '#eef1f4' }} />

          <p style={noteHeadingStyle}>
            Reading this alongside the clotting markers
          </p>
          <p style={noteStyle}>{COMPANION_INTRO}</p>
          <table style={sensitivityTableStyle}>
            <tbody>
              {SENSITIVITY_ROWS.map(r => (
                <tr key={r.input}>
                  <td style={sensitivityCellStyle}>{r.input}</td>
                  <td style={sensitivityCellStyle}>{r.span}</td>
                  <td style={sensitivityCellStyle}>{r.effect}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ ...noteStyle, marginBottom: 0 }}>{COMPANION_OUTRO}</p>
        </Card.Body>
      </Card>
    );
  }
}
